import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, TextField, Button, Box, FormControl,
  FormLabel, RadioGroup, FormControlLabel, Radio,
  Select, MenuItem, InputLabel, Chip, Stack,
  Alert, Fade, LinearProgress, Card, CardContent,
  CardMedia, Grid, Avatar, useTheme, useMediaQuery,
  Slide, Accordion, AccordionSummary, AccordionDetails,
  IconButton
} from '@mui/material';
import {
  PhotoCamera, SmartToy, Category, Schedule,
  ExpandMore, Close, CheckCircle, Error,
  TrendingUp, Assessment
} from '@mui/icons-material';
import { submitClassificationFeedback } from '../../api/classify';
import { getUserFeedbackStats } from '../../api/user';

const WASTE_CATEGORIES = [
  'Compost',
  'Recycle',
  'Hazardous',
  'General_Waste',
  'Unknown / Still learning'
];

const FeedbackDialog = ({
  open,
  onClose,
  classificationResult,
  binColor = '#43a047',
  onFeedbackSubmitted
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    message: '',
    correct: null,
    providedCategory: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userFeedbackStats, setUserFeedbackStats] = useState(null);
  const [detailsExpanded, setDetailsExpanded] = useState(!isMobile);

  // Fetch user feedback statistics when dialog opens
  useEffect(() => {
    const fetchFeedbackStats = async () => {
      if (open) {
        try {
          const response = await getUserFeedbackStats();
          setUserFeedbackStats(response.data);
        } catch (err) {
          console.error('Failed to fetch feedback stats:', err);
        }
      }
    };

    fetchFeedbackStats();
  }, [open]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        message: '',
        correct: null,
        providedCategory: ''
      });
      setError('');
      setSuccess('');
      setDetailsExpanded(!isMobile);
    }
  }, [open, isMobile]);

  const handleSubmit = async () => {
    if (!formData.message.trim()) {
      setError('Please provide feedback message');
      return;
    }

    if (formData.correct === null) {
      setError('Please indicate if the classification was correct');
      return;
    }

    if (formData.correct === false && !formData.providedCategory) {
      setError('Please select the correct category');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await submitClassificationFeedback({
        classificationId: classificationResult?.id,
        message: formData.message,
        correct: formData.correct,
        providedCategory: formData.correct === false ? formData.providedCategory : null
      });

      setSuccess('✅ Thank you for your valuable feedback! This helps improve our AI model.');

      // Call parent callback if provided
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }

      // Close dialog after a delay
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      setError(err?.response?.data?.message || '❌ Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCorrectChange = (event) => {
    const isCorrect = event.target.value === 'true';
    setFormData(prev => ({
      ...prev,
      correct: isCorrect,
      providedCategory: isCorrect ? '' : prev.providedCategory
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? 'sm' : 'lg'}
      fullWidth
      fullScreen={isSmallMobile}
      PaperProps={{
        sx: {
          borderRadius: isSmallMobile ? 0 : 6,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8faf8 100%)',
          border: isSmallMobile ? 'none' : `3px solid ${binColor}20`,
          m: isSmallMobile ? 0 : 2,
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
      TransitionComponent={isMobile ? Slide : undefined}
      TransitionProps={isMobile ? { direction: 'up' } : {}}
    >
      <DialogTitle
        sx={{
          pb: isMobile ? 1 : 2,
          pt: isSmallMobile ? 3 : 3,
          px: { xs: 2, sm: 3 },
          position: 'relative'
        }}
      >
        {/* Mobile Close Button */}
        {isMobile && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'text.secondary',
              bgcolor: 'rgba(0,0,0,0.1)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' }
            }}
          >
            <Close />
          </IconButton>
        )}

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 1.5, sm: 2 },
          flexDirection: isSmallMobile ? 'column' : 'row',
          textAlign: isSmallMobile ? 'center' : 'left'
        }}>
          <Box
            sx={{
              width: { xs: 50, sm: 60 },
              height: { xs: 50, sm: 60 },
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${binColor} 0%, ${binColor}CC 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 6px 25px ${binColor}40`,
              flexShrink: 0
            }}
          >
            <Typography fontSize={{ xs: '1.5rem', sm: '2rem' }}>💭</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isSmallMobile ? 'h5' : 'h4'}
              fontWeight={800}
              sx={{
                color: binColor,
                fontSize: { xs: '1.3rem', sm: '1.8rem', md: '2rem' },
                lineHeight: 1.2
              }}
            >
              🤔 Help Us Improve AI
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              fontWeight={500}
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem' },
                mt: 0.5
              }}
            >
              Your feedback trains our AI for everyone
            </Typography>
          </Box>
        </Box>

        {/* User Feedback Statistics - Mobile Optimized */}
        {userFeedbackStats && (
          <Box sx={{
            background: `linear-gradient(135deg, ${binColor}10 0%, ${binColor}05 100%)`,
            borderRadius: { xs: 3, sm: 4 },
            p: { xs: 2, sm: 3 },
            border: `2px solid ${binColor}20`
          }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                color: binColor,
                mb: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              🏆 Your AI Training Contribution
            </Typography>
            <Stack
              direction={isSmallMobile ? 'column' : 'row'}
              spacing={{ xs: 1, sm: 2 }}
              flexWrap="wrap"
            >
              <Chip
                icon={<TrendingUp />}
                label={`${userFeedbackStats.totalFeedback} Total`}
                size={isSmallMobile ? 'small' : 'medium'}
                sx={{
                  bgcolor: '#4caf50',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' },
                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                }}
              />
              <Chip
                icon={<CheckCircle />}
                label={`${userFeedbackStats.correctFeedback} Correct`}
                size={isSmallMobile ? 'small' : 'medium'}
                sx={{
                  bgcolor: '#2196f3',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' },
                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                }}
              />
              <Chip
                icon={<Assessment />}
                label={`${userFeedbackStats.incorrectFeedback} Improvements`}
                size={isSmallMobile ? 'small' : 'medium'}
                sx={{
                  bgcolor: '#ff9800',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' },
                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                }}
              />
            </Stack>
            {userFeedbackStats.totalFeedback > 0 && (
              <Typography
                variant="body2"
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  color: binColor,
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                }}
              >
                🌟 Thank you for being an AI training champion! Your feedback makes Sortify smarter for everyone.
              </Typography>
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{
        pt: 0,
        px: { xs: 2, sm: 3 },
        pb: { xs: 1, sm: 2 }
      }}>
        {isMobile ? (
          /* Mobile Layout - Single Column with Accordion */
          <Stack spacing={{ xs: 2, sm: 3 }}>
            {/* Collapsible Classification Details for Mobile */}
            <Accordion
              expanded={detailsExpanded}
              onChange={() => setDetailsExpanded(!detailsExpanded)}
              sx={{
                borderRadius: 3,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: `${binColor}05`,
                  borderRadius: detailsExpanded ? '12px 12px 0 0' : '12px',
                  minHeight: 56
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhotoCamera sx={{ color: binColor }} />
                  <Typography variant="h6" fontWeight={600}>
                    View Classification Details
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Mobile-Optimized Classification Details */}
                <Stack spacing={2}>
                  {/* Classification Image - Mobile Optimized */}
                  {classificationResult?.imageUrl && (
                    <Box sx={{ textAlign: 'center' }}>
                      <CardMedia
                        component="img"
                        image={classificationResult.imageUrl}
                        alt="Classified Item"
                        sx={{
                          borderRadius: 2,
                          maxHeight: { xs: 200, sm: 250 },
                          maxWidth: '100%',
                          objectFit: 'contain',
                          border: '2px solid #e0e0e0',
                          bgcolor: '#f9f9f9'
                        }}
                      />
                    </Box>
                  )}

                  {/* Classification Info - Mobile Grid */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{
                        p: 2,
                        bgcolor: '#f8f9fa',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          AI PREDICTION
                        </Typography>
                        <Chip
                          label={classificationResult?.category?.name || 'Unknown'}
                          sx={{
                            backgroundColor: classificationResult?.category?.color ? `${classificationResult.category.color}20` : '#f5f5f5',
                            color: classificationResult?.category?.color || '#666',
                            fontWeight: 600,
                            mt: 1,
                            display: 'block',
                            maxWidth: '100%'
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{
                        p: 2,
                        bgcolor: '#f8f9fa',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          CONFIDENCE
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                            {Math.round(classificationResult?.resultConfidence || 0)}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={classificationResult?.resultConfidence || 0}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: binColor,
                                borderRadius: 3
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {classificationResult?.aiModelVersion && (
                      <Grid item xs={6}>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            MODEL
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                            {classificationResult.aiModelVersion.versionName}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={6}>
                      <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          DATE
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                          {formatDate(classificationResult?.classifiedAt)}
                        </Typography>
                      </Box>
                    </Grid>

                    {classificationResult?.suggestion && (
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            AI SUGGESTION
                          </Typography>
                          <Typography variant="body2" sx={{
                            mt: 1,
                            fontStyle: 'italic',
                            lineHeight: 1.4
                          }}>
                            "{classificationResult.suggestion}"
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Mobile-Optimized Feedback Form */}
            <Stack spacing={{ xs: 2.5, sm: 3 }}>
              {/* Was Classification Correct? - Mobile */}
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    color: binColor,
                    mb: 1
                  }}
                >
                  Was this AI classification correct? *
                </FormLabel>
                <RadioGroup
                  value={formData.correct === null ? '' : formData.correct.toString()}
                  onChange={handleCorrectChange}
                  sx={{ gap: 1 }}
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio sx={{ color: binColor }} />}
                    label={
                      <Box sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography fontSize="1.2rem">✅</Typography>
                          <Typography fontWeight={600} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Yes, the classification was correct
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          The AI correctly identified this item
                        </Typography>
                      </Box>
                    }
                    sx={{
                      bgcolor: formData.correct === true ? `${binColor}08` : 'transparent',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      border: formData.correct === true ? `2px solid ${binColor}30` : '2px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio sx={{ color: binColor }} />}
                    label={
                      <Box sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography fontSize="1.2rem">❌</Typography>
                          <Typography fontWeight={600} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            No, the classification was incorrect
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          The AI made a mistake - help us learn the correct answer
                        </Typography>
                      </Box>
                    }
                    sx={{
                      bgcolor: formData.correct === false ? '#ffebee' : 'transparent',
                      borderRadius: 2,
                      p: 1,
                      m: 0,
                      border: formData.correct === false ? '2px solid #ffcdd2' : '2px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                  />
                </RadioGroup>
              </FormControl>

              {/* Correct Category Selection - Mobile Optimized */}
              {formData.correct === false && (
                <Fade in={formData.correct === false} timeout={500}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontWeight: 600 }}>What should be the correct category? *</InputLabel>
                    <Select
                      value={formData.providedCategory}
                      onChange={(e) => setFormData(prev => ({ ...prev, providedCategory: e.target.value }))}
                      label="What should be the correct category? *"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: `${binColor}40`,
                          borderWidth: 2
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: binColor
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: binColor
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              py: 1.5,
                              borderBottom: '1px solid #f0f0f0'
                            }
                          }
                        }
                      }}
                    >
                      {WASTE_CATEGORIES.map((category) => (
                        <MenuItem key={category} value={category}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                            <Typography sx={{ fontSize: '1.2rem' }}>
                              {category === 'Compost' && '🌱'}
                              {category === 'Recycle' && '♻️'}
                              {category === 'Hazardous' && '⚠️'}
                              {category === 'General_Waste' && '🗑️'}
                              {category === 'Unknown / Still learning' && '❓'}
                            </Typography>
                            <Typography fontWeight={500}>{category}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Fade>
              )}

              {/* Feedback Message - Mobile Optimized */}
              <TextField
                label="Your detailed feedback *"
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder={
                  formData.correct === true
                    ? "Great classification! Share what made this result helpful..."
                    : formData.correct === false
                    ? "Please explain why this was incorrect and provide details..."
                    : "Please provide your feedback about this classification..."
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${binColor}40`,
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: binColor
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: binColor
                    }
                  },
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />

              {/* AI Training Impact Notice - Mobile */}
              <Alert
                severity="info"
                sx={{
                  borderRadius: 3,
                  border: `2px solid ${binColor}30`,
                  backgroundColor: `${binColor}05`,
                  '& .MuiAlert-message': {
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  🤖 AI Training Impact:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <li>Your feedback becomes part of our training dataset</li>
                  <li>Incorrect classifications help the AI learn from mistakes</li>
                  <li>All feedback is reviewed before model updates</li>
                </Box>
              </Alert>
            </Stack>
          </Stack>
        ) : (
          /* Desktop Layout - Two Column Grid */
          <Grid container spacing={3}>
            {/* Classification Details */}
            <Grid item xs={12} md={5}>
              <Card elevation={3} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhotoCamera /> Original Classification
                  </Typography>

                  {/* Classification Image */}
                  {classificationResult?.imageUrl && (
                    <Box sx={{ mb: 2 }}>
                      <CardMedia
                        component="img"
                        image={classificationResult.imageUrl}
                        alt="Classified Item"
                        sx={{
                          borderRadius: 2,
                          maxHeight: 200,
                          objectFit: 'contain',
                          width: '100%',
                          border: '2px solid #e0e0e0'
                        }}
                      />
                    </Box>
                  )}

                  {/* Classification Details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">AI Prediction</Typography>
                      <Chip
                        label={classificationResult?.category?.name || 'Unknown'}
                        sx={{
                          backgroundColor: classificationResult?.category?.color ? `${classificationResult.category.color}20` : '#f5f5f5',
                          color: classificationResult?.category?.color || '#666',
                          fontWeight: 600,
                          mt: 0.5
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">Confidence Level</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="h6" fontWeight={700}>
                          {Math.round(classificationResult?.resultConfidence || 0)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={classificationResult?.resultConfidence || 0}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: binColor,
                              borderRadius: 4
                            }
                          }}
                        />
                      </Box>
                    </Box>

                    {classificationResult?.aiModelVersion && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">AI Model</Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {classificationResult.aiModelVersion.versionName}
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="body2" color="text.secondary">Classification Date</Typography>
                      <Typography variant="body1">
                        {formatDate(classificationResult?.classifiedAt)}
                      </Typography>
                    </Box>

                    {classificationResult?.suggestion && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">AI Suggestion</Typography>
                        <Typography variant="body2" sx={{
                          backgroundColor: '#f8f9fa',
                          p: 2,
                          borderRadius: 1,
                          mt: 0.5,
                          fontStyle: 'italic'
                        }}>
                          "{classificationResult.suggestion}"
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Feedback Form */}
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                {/* Was Classification Correct? */}
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: '1.1rem', color: binColor }}>
                    Was this AI classification correct? *
                  </FormLabel>
                  <RadioGroup
                    value={formData.correct === null ? '' : formData.correct.toString()}
                    onChange={handleCorrectChange}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio sx={{ color: binColor }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontSize="1.2rem">✅</Typography>
                          <Box>
                            <Typography fontWeight={600}>Yes, the classification was correct</Typography>
                            <Typography variant="caption" color="text.secondary">
                              The AI correctly identified this item
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio sx={{ color: binColor }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontSize="1.2rem">❌</Typography>
                          <Box>
                            <Typography fontWeight={600}>No, the classification was incorrect</Typography>
                            <Typography variant="caption" color="text.secondary">
                              The AI made a mistake - help us learn the correct answer
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {/* Correct Category Selection (only if incorrect) */}
                {formData.correct === false && (
                  <Fade in={formData.correct === false} timeout={500}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ fontWeight: 600 }}>What should be the correct category? *</InputLabel>
                      <Select
                        value={formData.providedCategory}
                        onChange={(e) => setFormData(prev => ({ ...prev, providedCategory: e.target.value }))}
                        label="What should be the correct category? *"
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${binColor}40`,
                            borderWidth: 2
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: binColor
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: binColor
                          }
                        }}
                      >
                        {WASTE_CATEGORIES.map((category) => (
                          <MenuItem key={category} value={category}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography>
                                {category === 'Compost' && '🌱'}
                                {category === 'Recycle' && '♻️'}
                                {category === 'Hazardous' && '⚠️'}
                                {category === 'General_Waste' && '🗑️'}
                                {category === 'Unknown / Still learning' && '❓'}
                              </Typography>
                              <Typography>{category}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Fade>
                )}

                {/* Feedback Message */}
                <TextField
                  label="Your detailed feedback *"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={
                    formData.correct === true
                      ? "Great classification! You can share what made this result helpful or suggest improvements..."
                      : formData.correct === false
                      ? "Please explain why this classification was incorrect and provide details about the correct category..."
                      : "Please provide your feedback about this classification..."
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: `${binColor}40`,
                        borderWidth: 2
                      },
                      '&:hover fieldset': {
                        borderColor: binColor
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: binColor
                      }
                    }
                  }}
                />

                {/* AI Training Impact Notice */}
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                    border: `2px solid ${binColor}30`,
                    backgroundColor: `${binColor}05`
                  }}
                >
                  <Typography variant="body2">
                    <strong>🤖 AI Training Impact:</strong>
                    <br />• Your feedback becomes part of our training dataset
                    <br />• Incorrect classifications help the AI learn from mistakes
                    <br />• Positive feedback reinforces good predictions
                    <br />• All feedback is reviewed by our team before model updates
                  </Typography>
                </Alert>
              </Stack>
            </Grid>
          </Grid>
        )}

        {/* Loading Progress */}
        {loading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: `${binColor}20`,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: binColor
                }
              }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                textAlign: 'center',
                color: binColor,
                fontSize: { xs: '0.8rem', sm: '0.9rem' }
              }}
            >
              Submitting your valuable feedback...
            </Typography>
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Fade in={!!error} timeout={300}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 3,
                fontWeight: 600,
                mt: 2
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Success Message */}
        {success && (
          <Fade in={!!success} timeout={300}>
            <Alert
              severity="success"
              sx={{
                borderRadius: 3,
                fontWeight: 600,
                mt: 2
              }}
            >
              {success}
            </Alert>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: { xs: 2, sm: 3 },
        pt: 1,
        flexDirection: isSmallMobile ? 'column' : 'row',
        gap: isSmallMobile ? 1 : 0
      }}>
        <Button
          onClick={onClose}
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            order: isSmallMobile ? 2 : 1,
            width: isSmallMobile ? '100%' : 'auto'
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.message.trim() || formData.correct === null}
          sx={{
            fontWeight: 700,
            px: { xs: 3, sm: 4 },
            py: { xs: 1.5, sm: 1 },
            background: `linear-gradient(45deg, ${binColor} 30%, ${binColor}CC 90%)`,
            boxShadow: `0 6px 20px ${binColor}40`,
            order: isSmallMobile ? 1 : 2,
            width: isSmallMobile ? '100%' : 'auto',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            '&:hover': {
              background: `linear-gradient(45deg, ${binColor}DD 30%, ${binColor}AA 90%)`,
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 25px ${binColor}50`
            },
            '&:disabled': {
              background: '#9e9e9e',
              color: 'white'
            }
          }}
        >
          {loading ? 'Submitting...' : (isSmallMobile ? 'Submit Feedback' : 'Submit Feedback for AI Training')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
